/* eslint-disable array-callback-return */
import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  Button,
  Container,
  Dimmer,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Modal,
  Segment,
  Step,
  Table,
  TextArea,
} from "semantic-ui-react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

import { auth, db } from "../firebase/firebaseConfig";
import useObtenerIngreso from "../hooks/useObtenerIngreso";
import { UserContext } from "../context/UserContext";
import { Opcion } from "./TipDoc";

import generarTraspasoRetornoHomeCare from "../funciones/generarTraspasosRetornoHomeCare";

import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
  // getDoc,
} from "firebase/firestore";

const CATEGORIAS = {
  INSTRUMENTOS: "INSTRUMENTOS",
  CHECK: "CHECK",
  MEDICION: "MEDICION",
  SEGURIDAD: "SEGURIDAD",
};

const PROGRAMA = "ANUAL";

const toMillisSafe = (x) =>
  x?.toMillis ? x.toMillis() : x?.toDate ? x.toDate().getTime() : +new Date(x ?? 0);

const sortByFechaAddAsc = (a, b) => toMillisSafe(a?.fechaadd) - toMillisSafe(b?.fechaadd);

const toCLDateTime = (fsTimestamp) => {
  if (!fsTimestamp?.toDate) return "";
  return moment(fsTimestamp.toDate()).format("DD/MM/YYYY HH:mm");
};

const isEmpty = (v) => String(v ?? "").trim() === "";

const EjecutarMantencionSt = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // ✅ Hooks SIEMPRE arriba
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  const [ingreso] = useObtenerIngreso(id);

  // UI
  const [step, setStep] = useState(0); // 0=dato, 1=mantención
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openFinalConfirm, setOpenFinalConfirm] = useState(false);

  // Cabecera actual
  const cabeceraIdRef = useRef("");

  // Data state
  const [cabObs, setCabObs] = useState("");

  const [instrumentos, setInstrumentos] = useState([]);
  const [checkItems, setCheckItems] = useState([]);
  const [medicionItems, setMedicionItems] = useState([]);
  const [seguridadItems, setSeguridadItems] = useState([]);

  const volver = useCallback(() => {
    navigate("/serviciotecnico/asignadostecnicos");
  }, [navigate]);

  const datos = useMemo(() => {
    if (!ingreso) return null;
    return {
      folio: ingreso.folio ?? "",
      rut: ingreso.rut ?? "",
      entidad: ingreso.entidad ?? "",
      date: ingreso.date ?? null,
      telefono: ingreso.telefono ?? "",
      direccion: ingreso.direccion ?? "",
      correo: ingreso.correo ?? "",
      familia: ingreso.familia ?? "",
      tipo: ingreso.tipo ?? "",
      marca: ingreso.marca ?? "",
      modelo: ingreso.modelo ?? "",
      serie: ingreso.serie ?? "",
      servicio: ingreso.servicio ?? "",
      enproceso: ingreso.enproceso ?? "0",
    };
  }, [ingreso]);

  // Orden para mostrar
  const sortedInstrumentos = useMemo(() => [...instrumentos].sort(sortByFechaAddAsc), [instrumentos]);
  const sortedCheck = useMemo(() => [...checkItems].sort(sortByFechaAddAsc), [checkItems]);
  const sortedMedicion = useMemo(() => [...medicionItems].sort(sortByFechaAddAsc), [medicionItems]);
  const sortedSeguridad = useMemo(() => [...seguridadItems].sort(sortByFechaAddAsc), [seguridadItems]);

  const fetchDocs = useCallback(async (q) => {
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  }, []);

  // ✅ Update por ID (evita cruzar selecciones)
  const setCheckValorById = useCallback((itemId, valor) => {
    setCheckItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, valor } : it)));
  }, []);
  const setMedicionValorById = useCallback((itemId, valor) => {
    setMedicionItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, valor } : it)));
  }, []);
  const setSeguridadValorById = useCallback((itemId, valor) => {
    setSeguridadItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, valor } : it)));
  }, []);

  // ✅ CABECERA PENDIENTE (sin orderBy/limit -> no requiere índice)
  const getCabeceraPendientePorIngreso = useCallback(async () => {
    if (!users?.emp_id || !id) return null;

    const cabQ = query(
      collection(db, "bitacoracabst"),
      where("emp_id", "==", users.emp_id),
      where("id_ingreso", "==", id),
      where("confirmado", "==", false)
    );

    const cab = await fetchDocs(cabQ);

    if (cab.length > 0) {
      const sorted = [...cab].sort(
        (a, b) => toMillisSafe(b?.fechamod) - toMillisSafe(a?.fechamod)
      );
      cabeceraIdRef.current = sorted[0].id;
      setCabObs(sorted[0]?.observaciones ?? "");
      return sorted[0].id;
    }

    return null;
  }, [users?.emp_id, id, fetchDocs]);

  // Crear cabecera pendiente (si falta)
  const createCabeceraPendiente = useCallback(async () => {
    if (!users?.emp_id) throw new Error("No hay emp_id.");
    if (!datos?.tipo) throw new Error("No hay tipo en ingreso (datos.tipo).");

    const now = new Date();

    // ✅ Protocolo cabecera confirmado
    const protCabQ = query(
      collection(db, "protocoloscab"),
      where("emp_id", "==", users.emp_id),
      where("tipo", "==", datos.tipo),
      where("programa", "==", PROGRAMA),
      where("confirmado", "==", true)
    );

    const protocoloCab = await fetchDocs(protCabQ);

    if (protocoloCab.length === 0) {
      await Swal.fire(
        "Sin protocolo",
        "No existe un protocolo (cabecera) confirmado para este tipo/programa.",
        "warning"
      );
      return null;
    }

    const docRef = await addDoc(collection(db, "bitacoracabst"), {
      nombre_protocolo: protocoloCab[0].nombre,
      cab_id_protocolo: protocoloCab[0].id,
      fecha_mantencion: now,
      id_ingreso: id,
      folio_ingreso: datos.folio,
      confirmado: false,
      observaciones: "",
      useradd: user?.email ?? "",
      usermod: user?.email ?? "",
      fechaadd: now,
      fechamod: now,
      emp_id: users.emp_id,
    });

    cabeceraIdRef.current = docRef.id;
    setCabObs("");
    return docRef.id;
  }, [users?.emp_id, datos?.tipo, datos?.folio, id, fetchDocs, user?.email]);

  // Protocolos => plantilla (para primera vez)
  const loadFromProtocolos = useCallback(
    async (tipoParam) => {
      const tipoUsar = tipoParam || datos?.tipo;
      if (!users?.emp_id || !tipoUsar) return;

      const base = (categoria) =>
        query(
          collection(db, "protocolos"),
          where("emp_id", "==", users.emp_id),
          where("tipo", "==", tipoUsar),
          where("categoria", "==", categoria),
          where("programa", "==", PROGRAMA)
        );

      const [inst, chk, med, seg] = await Promise.all([
        fetchDocs(base(CATEGORIAS.INSTRUMENTOS)),
        fetchDocs(base(CATEGORIAS.CHECK)),
        fetchDocs(base(CATEGORIAS.MEDICION)),
        fetchDocs(base(CATEGORIAS.SEGURIDAD)),
      ]);

      setInstrumentos(inst);
      setCheckItems(chk.map((x) => ({ ...x, valor: x.valor ?? "" })));
      setMedicionItems(med.map((x) => ({ ...x, valor: x.valor ?? "" })));
      setSeguridadItems(seg.map((x) => ({ ...x, valor: x.valor ?? "" })));
    },
    [users?.emp_id, datos?.tipo, fetchDocs]
  );

  // Bitácoras => avance guardado
  const loadFromBitacoras = useCallback(async () => {
    if (!users?.emp_id || !cabeceraIdRef.current) return;

    const base = (categoria) =>
      query(
        collection(db, "bitacorasst"),
        where("emp_id", "==", users.emp_id),
        where("cab_id_bitacora", "==", cabeceraIdRef.current),
        where("categoria", "==", categoria)
      );

    const [inst, chk, med, seg] = await Promise.all([
      fetchDocs(base(CATEGORIAS.INSTRUMENTOS)),
      fetchDocs(base(CATEGORIAS.CHECK)),
      fetchDocs(base(CATEGORIAS.MEDICION)),
      fetchDocs(base(CATEGORIAS.SEGURIDAD)),
    ]);

    setInstrumentos(inst);
    setCheckItems(chk.map((x) => ({ ...x, valor: x.valor ?? "" })));
    setMedicionItems(med.map((x) => ({ ...x, valor: x.valor ?? "" })));
    setSeguridadItems(seg.map((x) => ({ ...x, valor: x.valor ?? "" })));
  }, [users?.emp_id, fetchDocs]);

  // Completo => habilita Finalizar
  const isComplete = useMemo(() => {
    const hasAny = checkItems.length > 0 || medicionItems.length > 0 || seguridadItems.length > 0;
    if (!hasAny) return false;

    return (
      !checkItems.some((x) => isEmpty(x.valor)) &&
      !medicionItems.some((x) => isEmpty(x.valor)) &&
      !seguridadItems.some((x) => isEmpty(x.valor)) &&
      !isEmpty(cabObs)
    );
  }, [checkItems, medicionItems, seguridadItems, cabObs]);

  const validateComplete = useCallback(() => {
    if (checkItems.some((x) => isEmpty(x.valor))) return "Items CHECK no pueden estar vacíos.";
    if (medicionItems.some((x) => isEmpty(x.valor))) return "Tabla MEDICIÓN no puede tener valores vacíos.";
    if (seguridadItems.some((x) => isEmpty(x.valor))) return "Tabla SEGURIDAD no puede tener valores vacíos.";
    if (isEmpty(cabObs)) return "Observaciones no puede estar vacío.";
    return null;
  }, [checkItems, medicionItems, seguridadItems, cabObs]);

  const isPasaMedicion = useCallback((it) => {
    const v = Number(it.valor);
    const ini = Number(it.inicial);
    const fin = Number(it.final);
    if (!Number.isFinite(v) || isEmpty(it.valor)) return null;
    return v >= ini && v <= fin;
  }, []);

  // ✅ Inicial: respeta enproceso y evita re-crear todo
  useEffect(() => {
    const run = async () => {
      if (!users?.emp_id || !datos?.tipo || !datos?.folio) return;

      setLoading(true);
      try {
        if (String(datos.enproceso) === "1") {
          let cabId = await getCabeceraPendientePorIngreso();
          if (!cabId) cabId = await createCabeceraPendiente();
          if (!cabId) {
            volver();
            return;
          }
          await loadFromBitacoras();
          setStep(1);
        } else {
          await loadFromProtocolos(datos.tipo);
          setStep(0);
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error cargando datos", String(e), "error");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    users?.emp_id,
    datos?.tipo,
    datos?.folio,
    datos?.enproceso,
    getCabeceraPendientePorIngreso,
    createCabeceraPendiente,
    loadFromBitacoras,
    loadFromProtocolos,
    volver,
  ]);

  // Siguiente: crea/recupera cabecera y pasa a mantención
  const onSiguiente = useCallback(async () => {
    setLoading(true);
    try {
      let cabId = await getCabeceraPendientePorIngreso();
      if (!cabId) cabId = await createCabeceraPendiente();
      if (!cabId) {
        volver();
        return;
      }
      setStep(1);
      Swal.fire({ icon: "success", title: "Proceda a realizar la mantención", timer: 900, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error", String(e), "error");
    } finally {
      setLoading(false);
    }
  }, [getCabeceraPendientePorIngreso, createCabeceraPendiente, volver]);

  // Guardar avance (create vs update)
  const upsertDetallesYObs = useCallback(async () => {
    if (!cabeceraIdRef.current) throw new Error("No existe cabecera de bitácora.");
    if (!users?.emp_id) throw new Error("emp_id no disponible.");

    const now = new Date();
    const bitacoraRef = collection(db, "bitacorasst");

    // ¿existe detalle? (revisamos CHECK)
    const existingCheckQ = query(
      collection(db, "bitacorasst"),
      where("emp_id", "==", users.emp_id),
      where("cab_id_bitacora", "==", cabeceraIdRef.current),
      where("categoria", "==", CATEGORIAS.CHECK)
    );
    const existingCheck = await fetchDocs(existingCheckQ);
    const isFirstSave = existingCheck.length === 0;

    const batch = writeBatch(db);

    if (isFirstSave) {
      // crea instrumentos
      instrumentos.forEach((x) => {
        const ref = doc(bitacoraRef);
        batch.set(ref, {
          item: x.item,
          valor: "",
          categoria: x.categoria,
          cab_id_bitacora: cabeceraIdRef.current,
          useradd: user?.email ?? "",
          usermod: user?.email ?? "",
          fechaadd: x.fechaadd ?? now,
          fechamod: now,
          emp_id: users.emp_id,
        });
      });

      // crea check
      checkItems.forEach((x) => {
        const ref = doc(bitacoraRef);
        batch.set(ref, {
          item: x.item,
          valor: x.valor ?? "",
          categoria: x.categoria,
          cab_id_bitacora: cabeceraIdRef.current,
          useradd: user?.email ?? "",
          usermod: user?.email ?? "",
          fechaadd: x.fechaadd ?? now,
          fechamod: now,
          emp_id: users.emp_id,
        });
      });

      // crea medición
      medicionItems.forEach((x) => {
        const ref = doc(bitacoraRef);
        batch.set(ref, {
          item: x.item,
          valor: x.valor ?? "",
          medida: x.medida,
          inicial: x.inicial,
          final: x.final,
          categoria: x.categoria,
          cab_id_bitacora: cabeceraIdRef.current,
          useradd: user?.email ?? "",
          usermod: user?.email ?? "",
          fechaadd: x.fechaadd ?? now,
          fechamod: now,
          emp_id: users.emp_id,
        });
      });

      // crea seguridad
      seguridadItems.forEach((x) => {
        const ref = doc(bitacoraRef);
        batch.set(ref, {
          item: x.item,
          valor: x.valor ?? "",
          categoria: x.categoria,
          cab_id_bitacora: cabeceraIdRef.current,
          useradd: user?.email ?? "",
          usermod: user?.email ?? "",
          fechaadd: x.fechaadd ?? now,
          fechamod: now,
          emp_id: users.emp_id,
        });
      });
    } else {
      // update
      checkItems.forEach((x) => {
        if (!x.id) return;
        batch.update(doc(db, "bitacorasst", x.id), { valor: x.valor ?? "", usermod: user?.email ?? "", fechamod: now });
      });
      medicionItems.forEach((x) => {
        if (!x.id) return;
        batch.update(doc(db, "bitacorasst", x.id), { valor: x.valor ?? "", usermod: user?.email ?? "", fechamod: now });
      });
      seguridadItems.forEach((x) => {
        if (!x.id) return;
        batch.update(doc(db, "bitacorasst", x.id), { valor: x.valor ?? "", usermod: user?.email ?? "", fechamod: now });
      });
    }

    // obs en cabecera
    batch.update(doc(db, "bitacoracabst", cabeceraIdRef.current), {
      observaciones: cabObs ?? "",
      usermod: user?.email ?? "",
      fechamod: now,
    });

    await batch.commit();

    // ✅ marca enproceso SOLO al guardar
    await updateDoc(doc(db, "ingresostcab", id), {
      enproceso: "1",
      estado_servicio: "EJECUTANDO",
      usermod: user?.email ?? "",
      fechamod: now,
    });

    // para obtener IDs si fue primera vez
    await loadFromBitacoras();
  }, [users?.emp_id, cabObs, checkItems, medicionItems, seguridadItems, instrumentos, fetchDocs, loadFromBitacoras, id, user?.email]);

  const onGuardarAvance = useCallback(async () => {
    setSaving(true);
    try {
      await upsertDetallesYObs();
      Swal.fire({ icon: "success", title: "Avance guardado", timer: 1100, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error al guardar avance", String(e), "error");
    } finally {
      setSaving(false);
    }
  }, [upsertDetallesYObs]);

  const onFinalizarClick = useCallback(() => {
    const err = validateComplete();
    if (err) return Swal.fire("Faltan datos", err, "warning");
    setOpenFinalConfirm(true);
  }, [validateComplete]);

  const onFinalizarConfirmado = useCallback(async () => {
    setOpenFinalConfirm(false);
    setSaving(true);

    try {
      const now = new Date();

      // ==========================================
      // 1. GUARDAR DETALLES Y OBSERVACIONES
      // ==========================================

      await upsertDetallesYObs();

      // ==========================================
      // 2. FINALIZAR ORDEN DE INGRESO ST
      // ==========================================

      //==========================================
      // OBTENER EMPRESA
      //==========================================
      // const empresaSnap = await getDoc(doc(db, "empresas", users.emp_id));
      // if (!empresaSnap.exists()) {
      //   throw new Error("No se encontró la empresa.");
      // }
      // const empresa = empresaSnap.data();

      //==========================
      // STATUS DEL EQUIPO
      //==========================
      // const qStatus = query(collection(db, "status"),  where("serie", "==", dato.serie));
      // const statusSnap = await getDocs(qStatus);
      // if (statusSnap.empty) {throw new Error(`No se encontró el estado del equipo ${dato.serie}`)}
      // const statusData = statusSnap.docs[0].data();

      //==========================================
      // CREAR TRASPASO
      //==========================================
      // const nuevoFolio = await correlativos(users.emp_id, "traspasos");
      // const batch = writeBatch(db);

      // // CABECERA
      // const cabeceraRef = doc(collection(db, "salidasCab"));

      // batch.set(cabeceraRef, {
      //   numdoc: nuevoFolio,
      //   tipdoc: "TRASPASOS",
      //   date: now,
      //   tipoinout: "S.TECNICO A PACIENTE",

      //   rut: statusData.r_permanente,
      //   entidad: statusData.n_permanente,
      //   rutSt: empresa.rut,
      //   entidadSt; empresa.empresa,

      //   correo,
      //   patente,

      //   descripcion: "",
      //   tipMov: 4,
      //   url: "",

      //   confirmado: false,
      //   entregado: false,
      //   retirado: false,
      //   observacion: "",

      //   userAdd: user.email,
      //   userMod: user.email,
      //   fechaAdd: now,
      //   fechaMod: now,

      //   emp_id: dato.emp_id_hc,
      // });

      // // DETALLE
      // const detalleRef = doc(collection(db, "traspasosdet"));

      // batch.set(detalleRef, {
      //   numdoc: nuevoFolio,
      //   tipdoc: "TRASPASOS",
      //   date: now,
      //   tipoinout: "S.TECNICO A PACIENTE",

      //   rut: datos.rut,
      //   entidad: datos.entidad,

      //   correo,
      //   patente,

      //   cab_id: cabeceraRef.id,

      //   eq_id: item.id,  //???

      //   familia: datos.familia,
      //   tipo: datos.tipo,
      //   marca: datos.marca,
      //   modelo: datos.modelo,
      //   serie: datos.serie,

      //   rfid: "",

      //   tipmov: cabeceraRef.tipMov,

      //   observacion: "",
      //   historial: 0,
      //   confirmado: false,

      //   useradd: user.email,
      //   usermod: user.email,
      //   fechaadd: now,
      //   fechamod: now,

      //   emp_id: users.emp_id_hc,

      //   rut_st: rutSt,
      //   nombre_st: entidadSt,

      //   destino_st_tipo: esStInterno ? "INTERNO" : "EXTERNO",                          //???
      //   contrato_mantencion: esStInterno,                                              //???
      //   tipo_cubierto_contrato: tipoCubiertoContrato,                                  //???
      //   genera_ingreso_st: esStInterno,                                                //???
      //   ingreso_st_generado: false,                                                    //???
      //   id_ingreso_st: id,
      //   id_mantencion: cabeceraIdRef.current,
      //   genera_certificado: true,
      //   reset_mantencion: now,
      // });

      // await batch.commit();

      //==========================================
      // FINALIZAR MANTENCIÓN
      //==========================================
      await updateDoc(doc(db, "ingresostcab", id), {
        enproceso: "0",
        estado: "REALIZADO",
        estado_servicio: "REALIZADO",
        fecha_out: now,
        usermod: user?.email ?? "",
        fechamod: now,
      });

      // ==========================================
      // 3. CONFIRMAR BITACORA DE MANTENCION
      // ==========================================

      await updateDoc(
        doc(db, "bitacoracabst", cabeceraIdRef.current),
        {
          confirmado: true,
          usermod: user?.email ?? "",
          fechamod: now,
        }
      );

      // ==========================================
      // 4. VALIDAR SI CORRESPONDE RETORNO HOMECARE
      // ==========================================

      const esTraspasoInterno =
        ingreso?.origen_ingreso === "TRASPASO_ST_INTERNO";

      let retornoGenerado = false;

      if (
        esTraspasoInterno &&
        ingreso?.traspaso_retorno_generado !== true
      ) {
        try {
          const retorno = await generarTraspasoRetornoHomeCare({
            orden: {
              id,
              ...ingreso,
            },

            userEmail: user?.email ?? "",

            // CAMBIAR SI TU RUTA REAL DE TRASPASOS ES OTRA
            linkNotificacion: "/transacciones/traspasos",
          });

          retornoGenerado = retorno?.generado === true;

          console.log(
            "Resultado traspaso retorno HomeCare:",
            retorno
          );
        } catch (errorRetorno) {
          // IMPORTANTE:
          // la mantención YA fue realizada correctamente.
          // Un fallo en el retorno no debe decir que
          // la mantención fracasó.

          console.error(
            "Error generando traspaso retorno HomeCare:",
            errorRetorno
          );

          await Swal.fire({
            icon: "warning",
            title: "Mantención finalizada",
            html: `
            La mantención fue finalizada correctamente,
            pero no fue posible generar el traspaso
            automático de retorno a HomeCare.
            <br/><br/>
            <b>${errorRetorno?.message || String(errorRetorno)}</b>
          `,
            confirmButtonText: "Aceptar",
          });

          volver();
          return;
        }
      }

      // ==========================================
      // 5. MENSAJE FINAL
      // ==========================================

      if (esTraspasoInterno && retornoGenerado) {
        await Swal.fire({
          icon: "success",
          title: "Mantención finalizada con éxito",
          html: `
          La mantención fue finalizada correctamente.
          <br/><br/>
          Se generó automáticamente el
          <b>traspaso de retorno a HomeCare</b>.
          <br/><br/>
          El documento quedó pendiente para que
          HomeCare complete los datos del transportista.
        `,
          confirmButtonText: "Aceptar",
        });
      } else {
        await Swal.fire(
          "Mantención finalizada con éxito!",
          "",
          "success"
        );
      }

      volver();

    } catch (e) {
      console.error(
        "Error al finalizar mantención:",
        e
      );

      Swal.fire(
        "Error al finalizar",
        e?.message || String(e),
        "error"
      );

    } finally {
      setSaving(false);
    }
  }, [
    upsertDetallesYObs,
    id,
    ingreso,
    user?.email,
    volver,
  ]);

  if (!datos) {
    return (
      <Container>
        <Segment>
          <Message info icon>
            <Icon name="info circle" />
            <Message.Content>
              <Message.Header>Cargando ingreso...</Message.Header>
            </Message.Content>
          </Message>
        </Segment>
      </Container>
    );
  }

  return (
    <Container style={{ paddingBottom: 24 }}>
      <Segment
        style={{
          background: "#ffffff",
          border: "1px solid #e5e9ef",
          borderRadius: 9,
          boxShadow: "0 2px 8px rgba(23,43,77,0.04)",
          padding: "20px 24px",
          marginBottom: 18,
        }}
      >
        <Header
          as="h2"
          style={{
            margin: 0,
            color: "#172b4d",
            fontSize: 23,
            fontWeight: 700,
          }}
        >
          <Icon name="wrench" style={{ color: "#0d9da5" }} />
          <Header.Content>
            Ejecutar Mantención
            <Header.Subheader
              style={{
                color: "#66758c",
                fontSize: 13,
                fontWeight: 400,
                marginTop: 6,
              }}
            >
              Checklist / mediciones / seguridad
            </Header.Subheader>
          </Header.Content>
        </Header>
      </Segment>

      <Segment raised>
        <Step.Group fluid size="small">
          <Step active={step === 0} completed={step > 0}>
            <Icon name="address card" />
            <Step.Content>
              <Step.Title>Datos</Step.Title>
              <Step.Description>Cliente y equipo</Step.Description>
            </Step.Content>
          </Step>
          <Step active={step === 1} disabled={step < 1}>
            <Icon name="check square" />
            <Step.Content>
              <Step.Title>Mantención</Step.Title>
              <Step.Description>Checklist y mediciones</Step.Description>
            </Step.Content>
          </Step>
        </Step.Group>
      </Segment>

      <Dimmer active={loading || saving} inverted>
        <Loader>{saving ? "Guardando..." : "Cargando..."}</Loader>
      </Dimmer>

      <Segment raised>
        <Header as="h3" dividing>
          <Icon name="user" />
          <Header.Content>Información Cliente</Header.Content>
        </Header>

        <Table celled compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Folio</Table.HeaderCell>
              <Table.HeaderCell>Rut</Table.HeaderCell>
              <Table.HeaderCell>Nombre</Table.HeaderCell>
              <Table.HeaderCell>Fecha</Table.HeaderCell>
              <Table.HeaderCell>Teléfono</Table.HeaderCell>
              <Table.HeaderCell>Dirección</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>{datos.folio}</Table.Cell>
              <Table.Cell>{datos.rut}</Table.Cell>
              <Table.Cell>{datos.entidad}</Table.Cell>
              <Table.Cell>{datos.date ? toCLDateTime(datos.date) : "-"}</Table.Cell>
              <Table.Cell>{datos.telefono}</Table.Cell>
              <Table.Cell>{datos.direccion}</Table.Cell>
              <Table.Cell>{datos.correo}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        <Header as="h3" dividing>
          <Icon name="cubes" />
          <Header.Content>Información Equipo</Header.Content>
        </Header>

        <Table celled compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Familia</Table.HeaderCell>
              <Table.HeaderCell>Tipo</Table.HeaderCell>
              <Table.HeaderCell>Marca</Table.HeaderCell>
              <Table.HeaderCell>Modelo</Table.HeaderCell>
              <Table.HeaderCell>Serie</Table.HeaderCell>
              <Table.HeaderCell>Servicio</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>{datos.familia}</Table.Cell>
              <Table.Cell>{datos.tipo}</Table.Cell>
              <Table.Cell>{datos.marca}</Table.Cell>
              <Table.Cell>{datos.modelo}</Table.Cell>
              <Table.Cell>{datos.serie}</Table.Cell>
              <Table.Cell>{datos.servicio}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {sortedInstrumentos.length > 0 && (
          <Segment secondary>
            <Header as="h4">
              <Icon name="list ul" />
              <Header.Content>Instrumentos requeridos</Header.Content>
            </Header>
            <List bulleted>
              {sortedInstrumentos.map((it) => (
                <List.Item key={it.id} style={{ fontSize: 13 }}>
                  {it.item}
                </List.Item>
              ))}
            </List>
          </Segment>
        )}

        <Divider />

        {step === 0 && (
          <Button color="teal" icon labelPosition="right" onClick={onSiguiente} disabled={loading || saving}>
            Siguiente
            <Icon name="arrow right" />
          </Button>
        )}

        {step >= 1 && (
          <>
            {/* CHECK */}
            {sortedCheck.length > 0 && (
              <Segment>
                <Header as="h3">
                  <Icon name="check" />
                  <Header.Content>Checklist</Header.Content>
                </Header>

                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={1}>N°</Table.HeaderCell>
                      <Table.HeaderCell>Item</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center" width={6}>
                        Resultado
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {sortedCheck.map((it, idx) => (
                      <Table.Row key={it.id}>
                        <Table.Cell>{idx + 1}</Table.Cell>
                        <Table.Cell style={{ whiteSpace: "normal" }}>{it.item}</Table.Cell>
                        <Table.Cell textAlign="center">
                          <Button.Group size="tiny">
                            <Button
                              positive={it.valor === "Realizado"}
                              onClick={() => setCheckValorById(it.id, "Realizado")}
                            >
                              Realizado
                            </Button>
                            <Button
                              basic
                              color={it.valor === "NA" ? "blue" : undefined}
                              onClick={() => setCheckValorById(it.id, "NA")}
                            >
                              N/A
                            </Button>
                          </Button.Group>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            )}

            {/* MEDICION */}
            {sortedMedicion.length > 0 && (
              <Segment>
                <Header as="h3">
                  <Icon name="tachometer alternate" />
                  <Header.Content>Medición</Header.Content>
                </Header>

                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={1}>N°</Table.HeaderCell>
                      <Table.HeaderCell>Item</Table.HeaderCell>
                      <Table.HeaderCell width={3}>Valor</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center" width={3}>
                        Rango
                      </Table.HeaderCell>
                      <Table.HeaderCell width={2}>Unidad</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center" width={1}>
                        Pasa
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="center" width={1}>
                        No pasa
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {sortedMedicion.map((it, idx) => {
                      const pasa = isPasaMedicion(it);
                      return (
                        <Table.Row key={it.id}>
                          <Table.Cell>{idx + 1}</Table.Cell>
                          <Table.Cell style={{ whiteSpace: "normal" }}>{it.item}</Table.Cell>
                          <Table.Cell>
                            <Form.Input
                              type="number"
                              value={it.valor}
                              onChange={(e, d) => setMedicionValorById(it.id, d.value)}
                              size="small"
                            />
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {it.inicial} - {it.final}
                          </Table.Cell>
                          <Table.Cell>{it.medida}</Table.Cell>
                          <Table.Cell textAlign="center">
                            {pasa === true && <Icon name="check" color="green" />}
                            {pasa === null && <Icon name="minus" color="grey" />}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {pasa === false && <Icon name="close" color="red" />}
                            {pasa === null && <Icon name="minus" color="grey" />}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </Segment>
            )}

            {/* SEGURIDAD */}
            {sortedSeguridad.length > 0 && (
              <Segment>
                <Header as="h3">
                  <Icon name="shield alternate" />
                  <Header.Content>Seguridad eléctrica</Header.Content>
                </Header>

                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={1}>N°</Table.HeaderCell>
                      <Table.HeaderCell>Item</Table.HeaderCell>
                      <Table.HeaderCell width={6}>Valor</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {sortedSeguridad.map((it, idx) => (
                      <Table.Row key={it.id}>
                        <Table.Cell>{idx + 1}</Table.Cell>
                        <Table.Cell style={{ whiteSpace: "normal" }}>
                          {it.item === "CLASIFICACION" ? (
                            <Label color="blue">CLASIFICACIÓN</Label>
                          ) : (
                            it.item
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {it.item === "CLASIFICACION" ? (
                            <Form.Select
                              placeholder="Seleccione opción"
                              options={Opcion.map((o, i) => ({ key: i, text: o.text, value: o.text }))}
                              value={it.valor}
                              onChange={(e, d) => setSeguridadValorById(it.id, d.value)}
                            />
                          ) : (
                            <Form.Input
                              value={it.valor}
                              onChange={(e, d) => setSeguridadValorById(it.id, d.value)}
                              placeholder="Ingrese valor"
                            />
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            )}

            {/* OBS */}
            <Segment>
              <Header as="h4">
                <Icon name="sticky note" />
                <Header.Content>Observaciones</Header.Content>
              </Header>

              <Form>
                <TextArea
                  placeholder="Ingrese observación o detalles adicionales"
                  value={cabObs}
                  onChange={(e) => setCabObs(e.target.value)}
                  style={{ minHeight: 90 }}
                />
              </Form>
            </Segment>

            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <Button basic icon labelPosition="left" onClick={() => setStep(0)} disabled={saving || loading}>
                    <Icon name="arrow left" />
                    Volver a datos
                  </Button>
                </Grid.Column>

                <Grid.Column textAlign="right">
                  <Button color="teal" onClick={onGuardarAvance} disabled={saving || loading}>
                    <Icon name="save" /> Guardar avance
                  </Button>

                  <Button
                    color="green"
                    onClick={onFinalizarClick}
                    disabled={!isComplete || saving || loading}
                    title={!isComplete ? "Completa todos los campos para finalizar" : "Finalizar"}
                    style={{ marginLeft: 8 }}
                  >
                    <Icon name="flag checkered" /> Finalizar
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            {!isComplete && (
              <Message info icon style={{ marginTop: 12 }}>
                <Icon name="info circle" />
                <Message.Content>
                  <Message.Header>Puedes guardar avance en cualquier momento</Message.Header>
                  Para habilitar <b>Finalizar</b>, completa CHECK, MEDICIÓN, SEGURIDAD y OBSERVACIONES.
                </Message.Content>
              </Message>
            )}
          </>
        )}
      </Segment>

      {/* Confirmación final */}
      <Modal size="small" open={openFinalConfirm} onClose={() => setOpenFinalConfirm(false)}>
        <Modal.Header>Confirmar finalización</Modal.Header>
        <Modal.Content>
          <Message warning icon>
            <Icon name="warning circle" />
            <Message.Content>
              <Message.Header>¿Finalizar la mantención?</Message.Header>
              Se marcará el ingreso como <b>REALIZADO</b> y se confirmará la bitácora.
            </Message.Content>
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenFinalConfirm(false)}>
            <Icon name="close" /> Cancelar
          </Button>
          <Button color="green" onClick={onFinalizarConfirmado} disabled={saving}>
            <Icon name="check" /> Sí, finalizar
          </Button>
        </Modal.Actions>
      </Modal>

      <Segment basic>
        <Button fluid basic color="grey" onClick={volver} disabled={saving || loading}>
          <Icon name="arrow left" /> Volver
        </Button>
      </Segment>
    </Container>
  );
};

export default EjecutarMantencionSt;
